import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { AlertTriangle, BookOpen, Info, Landmark, Plus, ScrollText, Trash2 } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { calculateFaraid, madhhabLabel } from "@/lib/calculators/faraid/core";
import {
  type Heirs,
  type Madhhab,
  EMPTY_HEIRS,
  HEIR_LABELS,
  type HeirGroup,
} from "@/lib/calculators/faraid/types";
import {
  type LandPlot,
  type LandType,
  type LandTitleStatus,
  newPlot,
  convertLand,
  distributeLand,
  summariseTotals,
  hasIndivisibilityRisk,
  formatLand,
  SMALL_SHARE_SQFT_THRESHOLD,
} from "@/lib/calculators/faraid/landDistribution";
import { LAND_UNITS, UNITS_BY_ID } from "@/data/bdLandUnits";


type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Faraid (Islamic Inheritance) Calculator — Money & Land", bn: "ফরায়েজ ক্যালকুলেটর — টাকা ও জমি" },
  desc: {
    en: "Distribute money AND land/property by classical Sunni Faraid rules — Furud, Asabah, Hajb, Awl, Radd. Hanafi default for Bangladesh.",
    bn: "কুরআন ও শরীয়াহ অনুযায়ী টাকা এবং জমি/সম্পত্তি বণ্টন — ফরাইজ, আসাবা, হাজব, আউল, রাদ্দ। হানাফী মাযহাব ডিফল্ট।",
  },
  estate: { en: "Net distributable estate (BDT)", bn: "নীট বণ্টনযোগ্য সম্পত্তি (টাকা)" },
  estateHint: {
    en: "After deducting funeral costs, debts, and bequests (max 1/3).",
    bn: "জানাজা খরচ, ঋণ ও ওয়াসিয়াত (সর্বোচ্চ ১/৩) বাদ দিয়ে।",
  },
  madhhab: { en: "Madhhab", bn: "মাযহাব" },
  spouse: { en: "Spouse", bn: "জীবনসঙ্গী" },
  husband: { en: "Husband alive", bn: "স্বামী জীবিত" },
  children: { en: "Children", bn: "সন্তান" },
  parents: { en: "Parents", bn: "পিতা-মাতা" },
  grandparents: { en: "Grandparents", bn: "দাদা-দাদি / নানি" },
  grandchildren: { en: "Grandchildren (via son, only if no children)", bn: "পৌত্র-পৌত্রী (পুত্রের সন্তান, সন্তান না থাকলে)" },
  siblings: { en: "Siblings (only if no descendants, father, or paternal grandfather)", bn: "ভাই-বোন (সন্তান, পিতা বা দাদা না থাকলে)" },
  pgf: { en: "Paternal grandfather (only if no father)", bn: "দাদা (পিতা না থাকলে)" },
  pgm: { en: "Paternal grandmother", bn: "দাদি" },
  mgm: { en: "Maternal grandmother", bn: "নানি" },
  fatherAlive: { en: "Father alive", bn: "পিতা জীবিত" },
  motherAlive: { en: "Mother alive", bn: "মাতা জীবিত" },
  results: { en: "Cash distribution", bn: "নগদ বণ্টন" },
  table: {
    heir: { en: "Heir", bn: "উত্তরাধিকারী" },
    share: { en: "Share", bn: "ভগ্নাংশ" },
    decimal: { en: "Decimal", bn: "দশমিক" },
    amount: { en: "Total amount (BDT)", bn: "মোট অংক (টাকা)" },
    perPerson: { en: "Per person", bn: "প্রতি জনে" },
  },
  blocked: { en: "Heirs excluded (Hajb)", bn: "অংশহীন উত্তরাধিকারী (হাজব)" },
  why: { en: "Step-by-step reasoning", bn: "ধাপে ধাপে ব্যাখ্যা" },
  reset: { en: "Reset", bn: "রিসেট" },
  print: { en: "Print", bn: "প্রিন্ট" },
  awlTag: { en: "Awl applied — shares reduced proportionally.", bn: "আউল প্রয়োগ — সব ফরাইজ আনুপাতিকভাবে কমেছে।" },
  raddTag: { en: "Radd applied — surplus returned to fixed-share heirs (excluding spouse).", bn: "রাদ্দ প্রয়োগ — অবশিষ্ট ফরাইজ-অংশীদারদের ফিরিয়ে দেওয়া হয়েছে (স্বামী/স্ত্রী বাদে)।" },
  asabahTag: { en: "Residue distributed to Asabah (residuary heirs).", bn: "অবশিষ্ট আসাবা (পুরুষ-প্রাধান্য আত্মীয়)-দের মধ্যে।" },
  totalCheck: { en: "Total distributed", bn: "মোট বণ্টিত" },

  // --- Mode + Land mode strings ---
  mode: { en: "Estate type", bn: "সম্পত্তির ধরন" },
  modeMoney: { en: "Money", bn: "টাকা" },
  modeLand: { en: "Land", bn: "জমি" },
  modeMixed: { en: "Mixed", bn: "মিশ্র" },
  landSection: { en: "Land properties", bn: "জমি/সম্পত্তি" },
  landHint: {
    en: "Add each plot. The same Faraid fractions apply to every plot. Optional per-unit price unlocks BDT-equivalent figures.",
    bn: "প্রতিটি প্লট যোগ করুন। প্রতিটি প্লটে একই ফরাইজ ভগ্নাংশ প্রযোজ্য। প্রতি ইউনিট মূল্য দিলে টাকার সমপরিমাণ দেখানো হবে।",
  },
  addPlot: { en: "Add another property", bn: "আরো জমি যোগ করুন" },
  removePlot: { en: "Remove", bn: "মুছুন" },
  plotName: { en: "Property name / description", bn: "প্লটের নাম / বিবরণ" },
  plotLocation: { en: "Location (district / thana)", bn: "অবস্থান (জেলা / থানা)" },
  plotType: { en: "Land type", bn: "জমির ধরন" },
  plotArea: { en: "Area", bn: "পরিমাণ" },
  plotUnit: { en: "Unit", bn: "একক" },
  plotPrice: { en: "Price per unit (BDT, optional)", bn: "প্রতি ইউনিট মূল্য (টাকা, ঐচ্ছিক)" },
  plotTitle: { en: "Title status (optional)", bn: "মালিকানা অবস্থা (ঐচ্ছিক)" },
  plotIndivisible: { en: "Cannot be physically divided", bn: "ভৌতিকভাবে বিভক্ত করা যাবে না" },
  landResults: { en: "Land distribution", bn: "জমি বণ্টন" },
  landTotals: { en: "Total land", bn: "মোট জমি" },
  combinedValue: { en: "Combined value (cash + land)", bn: "সম্মিলিত মূল্য (নগদ + জমি)" },
  combinedNote: {
    en: "If land is sold and pooled with cash, each heir's total in BDT.",
    bn: "জমি বিক্রি করে নগদের সাথে যোগ করলে প্রতি উত্তরাধিকারীর প্রাপ্য মোট টাকা।",
  },
  indivisibleTitle: {
    en: "Physical division may not be possible",
    bn: "ভৌতিক বিভাজন সম্ভব নাও হতে পারে",
  },
  indivisibleSub: {
    en: "Some heir slices fall below ~200 sq ft. Consider these alternatives:",
    bn: "কিছু উত্তরাধিকারীর অংশ ~২০০ বর্গফুটের নিচে। নিচের বিকল্পগুলো বিবেচনা করুন:",
  },
};

const MADHHABS: { id: Madhhab; bn: string; en: string }[] = [
  { id: "hanafi", en: "Hanafi", bn: "হানাফী" },
  { id: "shafii", en: "Shafi'i", bn: "শাফি'ঈ" },
  { id: "maliki", en: "Maliki", bn: "মালিকী" },
  { id: "hanbali", en: "Hanbali", bn: "হাম্বলী" },
];

type Mode = "money" | "land" | "mixed";

const LAND_TYPE_LABELS: Record<Exclude<LandType, "">, Bilingual> = {
  agricultural: { en: "Agricultural", bn: "কৃষি জমি" },
  residential: { en: "Residential", bn: "আবাসিক" },
  commercial: { en: "Commercial", bn: "বাণিজ্যিক" },
  ancestral: { en: "Inherited (ancestral)", bn: "পৈতৃক" },
  other: { en: "Other", bn: "অন্যান্য" },
};

const TITLE_STATUS_LABELS: Record<Exclude<LandTitleStatus, "">, Bilingual> = {
  mutated: { en: "Mutated to deceased's name (Namjari done)", bn: "নামজারি আছে" },
  "not-mutated": { en: "Inherited but not mutated", bn: "পৈতৃক, নামজারি নেই" },
  disputed: { en: "Disputed", bn: "বিতর্কিত" },
  other: { en: "Other", bn: "অন্যান্য" },
};

const INDIVISIBLE_OPTIONS: { title: Bilingual; body: Bilingual }[] = [
  {
    title: { en: "Option 1: Sell the land and divide the proceeds", bn: "বিকল্প ১: জমি বিক্রি করে টাকা ভাগ করুন" },
    body: {
      en: "Each heir receives their fractional share of the sale proceeds in BDT — easiest when shares are very small.",
      bn: "প্রতিটি উত্তরাধিকারী বিক্রয়মূল্যের নির্দিষ্ট ভগ্নাংশ টাকায় পান — অংশ ছোট হলে সবচেয়ে সহজ।",
    },
  },
  {
    title: { en: "Option 2: One heir buys the others out", bn: "বিকল্প ২: একজন উত্তরাধিকারী অন্যদের কিনে নিন" },
    body: {
      en: "With family consent, one heir pays the others their BDT-equivalent and takes full ownership. Must be formalised by a registered Bainama deed.",
      bn: "পরিবারের সম্মতিতে একজন অন্যদের টাকা পরিশোধ করে পূর্ণ মালিকানা নেন। আনুষ্ঠানিক বায়নামা দলিল করতে হবে।",
    },
  },
  {
    title: { en: "Option 3: Partition Suit in court", bn: "বিকল্প ৩: পার্টিশন সুট" },
    body: {
      en: "If physical division is feasible but heirs disagree, a partition suit at the Assistant Judge Court resolves boundaries.",
      bn: "ভৌতিক বিভাজন সম্ভব হলেও মতবিরোধ থাকলে সহকারী জজ আদালতে পার্টিশন সুট দায়ের করুন।",
    },
  },
  {
    title: { en: "Option 4: Keep the land jointly owned", bn: "বিকল্প ৪: যৌথ মালিকানা রাখুন" },
    body: {
      en: "Heirs co-own the property and share rent or agricultural income according to their Faraid fractions.",
      bn: "উত্তরাধিকারীরা যৌথভাবে মালিক থাকেন; ভাড়া বা ফসলের আয় ফরাইজ অনুপাতে ভাগ করেন।",
    },
  },
];


const FAQ: { q: Bilingual; a: Bilingual }[] = [
  { q: { en: "What is Faraid?", bn: "ফরায়েজ কী?" }, a: { en: "Faraid is the Islamic science of inheritance — the rules in the Qur'an (Surah An-Nisa 4:11–12, 4:176) and Hadith for distributing a deceased Muslim's estate among heirs.", bn: "ফরায়েজ হলো ইসলামি উত্তরাধিকার বিজ্ঞান — কুরআন (সূরা আন-নিসা ৪:১১–১২, ৪:১৭৬) ও হাদীসে বর্ণিত মৃত মুসলিমের সম্পত্তি উত্তরাধিকারীদের মধ্যে বণ্টনের বিধান।" } },
  { q: { en: "Why does the wife get 1/8 in this calculator?", bn: "এখানে স্ত্রী ১/৮ কেন পান?" }, a: { en: "When the deceased has children (or grandchildren via son), the wife's Quranic share is 1/8 (shared equally if more than one wife). Without descendants she gets 1/4.", bn: "মৃতের সন্তান (বা পৌত্র-পৌত্রী) থাকলে স্ত্রী কুরআন অনুযায়ী ১/৮ পান (একাধিক হলে সমান ভাগ)। সন্তান না থাকলে ১/৪।" } },
  { q: { en: "Why does the son get twice the daughter's share?", bn: "পুত্র কেন কন্যার দ্বিগুণ পান?" }, a: { en: "Surah An-Nisa 4:11 explicitly sets the male share at twice the female's. The classical rationale is that the male bears full financial responsibility for his family, while the female's wealth remains her own.", bn: "সূরা আন-নিসা ৪:১১-তে স্পষ্টভাবে পুরুষের অংশ নারীর দ্বিগুণ ধরা হয়েছে। শাস্ত্রীয় ব্যাখ্যা: পুরুষের উপর পরিবার চালানোর সম্পূর্ণ আর্থিক দায়িত্ব থাকে; নারীর সম্পদ তার নিজস্ব।" } },
  { q: { en: "What is Awl?", bn: "আউল কী?" }, a: { en: "When fixed shares add up to more than 1, Awl proportionally reduces every fixed share so they fit. The classical example: 6/12 + 4/12 + 8/12 → denominator becomes 17 instead of 12.", bn: "যখন ফরাইজের যোগফল ১ এর বেশি হয়, তখন আউল প্রতিটি ফরাইজ আনুপাতিকভাবে কমিয়ে আনে। হর ১২ এর জায়গায় হয় ১৭।" } },
  { q: { en: "What is Radd?", bn: "রাদ্দ কী?" }, a: { en: "When fixed shares add up to less than 1 and there is no Asabah (residuary heir), the surplus is returned proportionally to the fixed-share heirs — except the spouse, who classically does not benefit from Radd.", bn: "ফরাইজের যোগফল ১ এর কম হলে এবং কোনো আসাবা না থাকলে, অবশিষ্ট অংশ স্বামী/স্ত্রী বাদে অন্য ফরাইজ-অংশীদারদের আনুপাতিকভাবে ফিরিয়ে দেওয়া হয়।" } },
  { q: { en: "What is the Umariyatayn case?", bn: "উমারিয়াতাইন কেস কী?" }, a: { en: "Spouse + Mother + Father (no descendants, no siblings). The mother gets 1/3 of the remainder after the spouse — not 1/3 of the whole estate. Named after Caliph Umar (RA)'s ruling.", bn: "স্বামী/স্ত্রী + মাতা + পিতা (সন্তান ও ভাই-বোন নেই)। মাতা পুরো সম্পত্তির ১/৩ নয়, স্ত্রী/স্বামীর অংশ বাদে অবশিষ্টের ১/৩ পান। খলিফা উমর (রা.)-এর সিদ্ধান্ত।" } },
  { q: { en: "Are daughters of a daughter heirs?", bn: "মেয়ের সন্তান কি উত্তরাধিকারী?" }, a: { en: "In standard Sunni Faraid, only descendants through a son inherit by Faraid. Daughter's children are 'Dhul-Arham' (distant kin) — they may inherit only when no Faraid heirs exist (Hanafi).", bn: "সাধারণ সুন্নী ফরাইজে কেবল পুত্রের সন্তানরা সরাসরি উত্তরাধিকার পায়। কন্যার সন্তান 'যবিল আরহাম' — কোনো ফরাইজ উত্তরাধিকারী না থাকলে হানাফী মতে পান।" } },
  { q: { en: "Can I make a will (Wasiyyah) for an heir?", bn: "উত্তরাধিকারীর জন্য কি ওয়াসিয়াত করা যায়?" }, a: { en: "No — a Muslim's bequest cannot exceed 1/3 of the estate, and cannot be made to an existing Quranic heir without unanimous consent of the other heirs. Use Hiba (gift) during your lifetime instead.", bn: "না — ওয়াসিয়াত মোট সম্পত্তির ১/৩ এর বেশি হতে পারে না, এবং ফরাইজ উত্তরাধিকারীর জন্য করা যায় না (অন্য সব উত্তরাধিকারীর সম্মতি ছাড়া)। জীবদ্দশায় হিবা (উপহার) করা যায়।" } },
  { q: { en: "What about adopted children?", bn: "দত্তক সন্তানের অবস্থা?" }, a: { en: "Adopted children are not heirs in Faraid — they keep their biological lineage. To benefit them you may bequeath up to 1/3 by Wasiyyah, or gift property during your lifetime.", bn: "দত্তক সন্তান ফরাইজে উত্তরাধিকারী নয় — তারা জৈবিক বংশের পরিচয়ই বহন করে। তাদের জন্য জীবদ্দশায় হিবা বা সর্বোচ্চ ১/৩ ওয়াসিয়াত করা যায়।" } },
  { q: { en: "Does the calculator follow Bangladesh law?", bn: "এটি কি বাংলাদেশের আইন অনুসরণ করে?" }, a: { en: "It applies Sharia Faraid as recognised under the Bangladesh Muslim Family Laws Ordinance 1961. The Ordinance modifies one classical rule (orphaned grandchildren — Section 4) which is not yet modelled here.", bn: "এটি বাংলাদেশের Muslim Family Laws Ordinance 1961-এর অধীন স্বীকৃত শরীয়াহ ফরাইজ অনুসরণ করে। অধ্যাদেশের সেকশন ৪ (এতিম পৌত্র-পৌত্রী) সংক্রান্ত পরিবর্তন এখানে এখনো অন্তর্ভুক্ত নয়।" } },
  { q: { en: "What about debts and funeral expenses?", bn: "ঋণ ও দাফন খরচ?" }, a: { en: "Funeral costs first, then debts, then bequests (max 1/3), THEN Faraid distribution. Enter the net distributable amount, not the gross estate.", bn: "প্রথমে দাফন খরচ, এরপর ঋণ পরিশোধ, এরপর ওয়াসিয়াত (সর্বোচ্চ ১/৩), তারপর ফরাইজ। গ্রস না, নীট অংক প্রবেশ করান।" } },
  { q: { en: "Does this account for all four madhhabs?", bn: "চারটি মাযহাব কি অন্তর্ভুক্ত?" }, a: { en: "v1 implements Hanafi (BD majority). For ordinary cases all four madhhabs agree. Edge cases like Mushtarakah, Akdariyya, and grandfather-vs-brothers competition differ — these are not yet modelled.", bn: "v1 হানাফী (BD সংখ্যাগরিষ্ঠ) প্রয়োগ করে। সাধারণ ক্ষেত্রে চার মাযহাব একই ফল দেয়। মুশতারাকা, আকদারিয়া, দাদা-ভাই প্রতিযোগিতার মতো বিশেষ ক্ষেত্রে পার্থক্য — এখনো অন্তর্ভুক্ত নয়।" } },
  { q: { en: "Why are siblings sometimes blocked?", bn: "ভাই-বোন কখনো বাদ পড়ে কেন?" }, a: { en: "Siblings inherit only in 'Kalala' situations: no descendants, no father, and (in Hanafi) no paternal grandfather. Otherwise the closer agnatic line excludes them.", bn: "ভাই-বোন কেবল 'কালালা' অবস্থায় পান: সন্তান নেই, পিতা নেই, এবং (হানাফী মতে) দাদাও নেই। অন্যথায় নিকটতর পুরুষ-বংশ তাদের বাদ দেয়।" } },
  { q: { en: "Is calculator output legally binding?", bn: "এই ফলাফল কি আইনত বাধ্যকর?" }, a: { en: "No. This is an educational tool. Before any actual distribution, consult a qualified Mufti and lawyer, and obtain a succession certificate from the appropriate court.", bn: "না। এটি শিক্ষামূলক টুল। প্রকৃত বণ্টনের আগে অভিজ্ঞ মুফতি ও আইনজীবীর সাথে পরামর্শ করুন এবং উপযুক্ত আদালত থেকে succession certificate নিন।" } },
  { q: { en: "Can a non-Muslim inherit?", bn: "অমুসলিম কি উত্তরাধিকারী হতে পারে?" }, a: { en: "Classical Sunni Faraid does not allow inheritance across religions. A non-Muslim relative can be benefited via Wasiyyah (up to 1/3) or Hiba during the deceased's lifetime.", bn: "শাস্ত্রীয় সুন্নী ফরাইজে ভিন্ন ধর্মের মধ্যে উত্তরাধিকার নেই। অমুসলিম আত্মীয়কে জীবদ্দশায় হিবা বা সর্বোচ্চ ১/৩ ওয়াসিয়াতের মাধ্যমে দেওয়া যায়।" } },

  // --- Land-focused FAQs ---
  { q: { en: "How is land distributed under Faraid?", bn: "জমি ফরাইদে কীভাবে বণ্টন হয়?" }, a: { en: "The exact same Quranic fractions that govern cash also govern land. If the wife's share is 1/8, she receives 1/8 of every plot — by area, not by 'one full plot'.", bn: "নগদের যে ভগ্নাংশ, জমিতেও সেই একই ভগ্নাংশ প্রযোজ্য। স্ত্রীর অংশ ১/৮ হলে প্রতিটি প্লটের ১/৮ অংশ (পরিমাণে) পাবেন — পুরো একটি প্লট নয়।" } },
  { q: { en: "5 katha land, 1 wife + 2 sons + 1 daughter — how to divide?", bn: "৫ কাঠা জমি, ১ স্ত্রী + ২ পুত্র + ১ কন্যা — কীভাবে বণ্টন?" }, a: { en: "Wife 1/8 = 0.625 katha (~1.03 decimal). Remaining 7/8 split 2:1:2:1 between sons & daughter (5 parts): each son 7/8 × 2/5 = 7/20 = 1.75 katha; daughter 7/40 = 0.875 katha. Sums back to 5 katha.", bn: "স্ত্রী ১/৮ = ০.৬২৫ কাঠা (~১.০৩ ডেসিমেল)। অবশিষ্ট ৭/৮ পুত্র-কন্যাদের ২ঃ১ অনুপাতে — প্রতি পুত্র ৭/২০ = ১.৭৫ কাঠা; কন্যা ৭/৪০ = ০.৮৭৫ কাঠা। মোট ৫ কাঠা।" } },
  { q: { en: "What if the land cannot be physically divided?", bn: "জমি ভৌতিকভাবে ভাগ করা না গেলে কী করব?" }, a: { en: "Four common options: (1) sell the plot and divide the proceeds; (2) one heir buys the others out via a registered Bainama deed; (3) file a partition suit at the Assistant Judge Court; or (4) keep joint ownership and split rent/income by Faraid fractions.", bn: "চারটি প্রচলিত বিকল্প: (১) জমি বিক্রি করে টাকা ভাগ; (২) একজন বায়নামা দলিলে অন্যদের কিনে নেন; (৩) সহকারী জজ আদালতে পার্টিশন সুট; (৪) যৌথ মালিকানা রেখে ভাড়া/আয় ফরাইজ অনুপাতে ভাগ।" } },
  { q: { en: "How do I do Namjari (mutation) for inherited land?", bn: "উত্তরাধিকার সূত্রে পাওয়া জমির নামজারি কীভাবে করব?" }, a: { en: "Apply at the Assistant Commissioner (Land) office (Upazila/Thana AC Land) with the death certificate, Warish Sanad (heirship certificate from Family Court), latest Khatian, and your NID. Khatian is then updated under heirs' names.", bn: "Upazila/Thana AC Land অফিসে আবেদন করুন — মৃত্যু সনদ, ওয়ারিশান সনদ (পারিবারিক আদালত থেকে), সর্বশেষ খতিয়ান এবং NID সহ। অনুমোদনের পর খতিয়ান উত্তরাধিকারীদের নামে হালনাগাদ হয়।" } },
  { q: { en: "Where do I get a Warish Sanad (heirship certificate)?", bn: "ওয়ারিশান সনদ কোথা থেকে নেব?" }, a: { en: "From the local Union Parishad / Pourashava / City Corporation, and for legal use a succession certificate from the Family Court. Both list all legal heirs with their Faraid shares.", bn: "স্থানীয় ইউনিয়ন পরিষদ / পৌরসভা / সিটি কর্পোরেশন থেকে — এবং আইনি প্রয়োজনে পারিবারিক আদালত থেকে succession certificate। দু'টিতেই সব উত্তরাধিকারী ও তাদের অংশ উল্লেখ থাকে।" } },
  { q: { en: "Do daughters inherit land equally with sons?", bn: "কন্যা কি ভাইয়ের সমান জমির ভাগ পান?" }, a: { en: "Daughters inherit half of what each son receives (Quran 4:11) — but they DO inherit, both cash and land, and their share is fully their own property protected by Sharia and BD law.", bn: "কন্যা পুত্রের অর্ধেক পান (কুরআন ৪:১১) — কিন্তু তিনি অবশ্যই পান, নগদ ও জমি উভয়ই, এবং তা তাঁর নিজস্ব সম্পত্তি — শরীয়াহ ও বাংলাদেশের আইনে সুরক্ষিত।" } },
  { q: { en: "Are ancestral and self-acquired land treated differently?", bn: "পৈতৃক ও স্বঅর্জিত জমির বণ্টনে পার্থক্য আছে?" }, a: { en: "No — under Islamic Faraid both are treated identically. Once title is in the deceased's name, the same fractions apply. Practically, ancestral plots may need extra title-verification before mutation.", bn: "না — ইসলামি ফরাইজে দু'টিই একইভাবে বণ্টিত হয়। মৃতের নামে দলিল থাকলে একই ভগ্নাংশ প্রযোজ্য। বাস্তবে পৈতৃক জমিতে নামজারির আগে title যাচাই বেশি জরুরি।" } },
  { q: { en: "What about a minor heir's land share?", bn: "নাবালক উত্তরাধিকারীর জমির অংশ?" }, a: { en: "A minor's share is held by their legal guardian. Selling or transferring a minor's land requires permission from the Family Court — the guardian alone cannot sign it away.", bn: "নাবালকের অংশ অভিভাবকের জিম্মায় থাকে। নাবালকের জমি বিক্রি বা হস্তান্তর করতে পারিবারিক আদালতের অনুমতি লাগে — অভিভাবক একা সিদ্ধান্ত নিতে পারেন না।" } },
];

export const Route = createFileRoute("/faraid-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "ফরায়েজ ক্যালকুলেটর — Islamic Inheritance Calculator BD",
      description:
        "ইসলামিক উত্তরাধিকার ক্যালকুলেটর — টাকা ও জমি (কাঠা/বিঘা/শতক) হানাফী মাযহাব অনুযায়ী বণ্টন। Faraid inheritance calculator for Bangladesh.",
      path: "/faraid-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Faraid Calculator", path: "/faraid-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: FaraidPage,
});

const COLORS = ["#0E7C3A", "#10B981", "#34D399", "#6EE7B7", "#A7F3D0",
  "#1E3A8A", "#3B82F6", "#60A5FA", "#93C5FD",
  "#92400E", "#D97706", "#F59E0B", "#FBBF24",
  "#7E22CE", "#A855F7", "#C084FC"];

function FaraidPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmtBdt = (v: number) => {
    const s = Math.round(v).toLocaleString("en-IN");
    return lang === "bn" ? `৳${toBengaliNumerals(s)}` : `৳${s}`;
  };

  useEffect(() => {
    if (!useAppStore.getState().langTouched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [estate, setEstate] = useState<number>(1_000_000);
  const [madhhab, setMadhhab] = useState<Madhhab>("hanafi");
  const [heirs, setHeirs] = useState<Heirs>({ ...EMPTY_HEIRS, wives: 1, sons: 1, daughters: 2 });
  const [mode, setMode] = useState<Mode>("mixed");
  const [plots, setPlots] = useState<LandPlot[]>(() => [
    { ...newPlot(), name: "Plot 1", amount: 5, unitId: "katha" },
  ]);

  const setH = <K extends keyof Heirs>(key: K, val: Heirs[K]) =>
    setHeirs((h) => {
      const next = { ...h, [key]: val };
      if (key === "husband" && val) next.wives = 0;
      if (key === "wives" && (val as number) > 0) next.husband = false;
      return next;
    });

  const updatePlot = (id: string, patch: Partial<LandPlot>) =>
    setPlots((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const addPlot = () => setPlots((ps) => [...ps, { ...newPlot(), name: `Plot ${ps.length + 1}` }]);
  const removePlot = (id: string) => setPlots((ps) => ps.filter((p) => p.id !== id));

  // Effective cash estate for the engine (0 in land-only mode → no cash lines)
  const effectiveEstate = mode === "land" ? 0 : estate;

  const result = useMemo(
    () => calculateFaraid(effectiveEstate, heirs, madhhab),
    [effectiveEstate, heirs, madhhab],
  );

  const total = result.shares.reduce((a, l) => a + l.amount, 0);
  const pieData = result.shares.map((l, i) => ({
    name: `${lang === "bn" ? HEIR_LABELS[l.group].bn : HEIR_LABELS[l.group].en}${l.count > 1 ? ` ×${l.count}` : ""}`,
    value: Math.max(l.fraction.toNumber(), 0.0001),
    color: COLORS[i % COLORS.length],
  }));

  const landTotals = useMemo(() => summariseTotals(plots), [plots]);
  const landRows = useMemo(() => distributeLand(result.shares, plots), [result.shares, plots]);
  const landIndivisible = useMemo(() => hasIndivisibilityRisk(landRows), [landRows]);
  const showLand = mode === "land" || mode === "mixed";
  const showCash = mode === "money" || mode === "mixed";

  const reset = () => {
    setHeirs({ ...EMPTY_HEIRS });
    setEstate(0);
    setPlots([{ ...newPlot(), name: "Plot 1", amount: 0, unitId: "katha" }]);
  };


  return (
    <CalculatorLayout
      slug="faraid-calculator"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="Scale"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      {/* Disclaimer banner */}
      <div className={`mb-6 flex gap-3 rounded-xl border-2 border-destructive/30 bg-destructive/5 p-4 text-sm ${lang === "bn" ? "font-bn" : ""}`}>
        <AlertTriangle size={20} className="mt-0.5 shrink-0 text-destructive" />
        <div>
          <p className="font-semibold text-destructive">
            {lang === "bn" ? "শিক্ষামূলক উদ্দেশ্যে।" : "For educational purposes only."}
          </p>
          <p className="mt-1 text-muted-foreground">
            {lang === "bn"
              ? "আইনি বণ্টনের আগে অভিজ্ঞ মুফতি ও আইনজীবীর সাথে পরামর্শ করুন। বাংলাদেশ Muslim Family Laws Ordinance 1961 ও শরীয়াহ অনুসরণ করুন।"
              : "Before any legal distribution, consult a qualified Mufti and lawyer. Refer to the Bangladesh Muslim Family Laws Ordinance 1961 and Sharia rules."}
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="mb-6">
        <div className={`mb-2 text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.mode)}</div>
        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList className="grid h-auto w-full grid-cols-3">
            <TabsTrigger value="money" className={`whitespace-normal py-1.5 text-xs leading-tight min-h-8 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.modeMoney)}</TabsTrigger>
            <TabsTrigger value="land" className={`whitespace-normal py-1.5 text-xs leading-tight min-h-8 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.modeLand)}</TabsTrigger>
            <TabsTrigger value="mixed" className={`whitespace-normal py-1.5 text-xs leading-tight min-h-8 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.modeMixed)}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Madhhab tabs */}
      <div className="mb-6">
        <div className={`mb-2 text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.madhhab)}</div>
        <Tabs value={madhhab} onValueChange={(v) => setMadhhab(v as Madhhab)}>
          <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-4">
            {MADHHABS.map((m) => (
              <TabsTrigger key={m.id} value={m.id} className={`whitespace-normal py-1.5 text-xs leading-tight min-h-8 ${lang === "bn" ? "font-bn" : ""}`}>
                {lang === "bn" ? m.bn : m.en}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Estate input (cash modes only) */}
      {showCash && (
        <div className="mb-6">
          <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.estate)}</label>
          <Input
            type="number"
            inputMode="decimal"
            value={estate || ""}
            onChange={(e) => setEstate(Math.max(0, Number(e.target.value) || 0))}
            min={0}
            className="text-lg"
          />
          <p className={`mt-1 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.estateHint)}</p>
        </div>
      )}

      {/* Land plots (land/mixed modes) */}
      {showLand && (
        <div className="mb-6">
          <div className="mb-2 flex items-center gap-2">
            <Landmark size={16} className="text-accent" />
            <span className={`text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.landSection)}</span>
          </div>
          <p className={`mb-3 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.landHint)}</p>
          <div className="space-y-3">
            {plots.map((plot, idx) => {
              const unit = UNITS_BY_ID[plot.unitId];
              const sqft = convertLand(plot.amount, plot.unitId, "sqft");
              const decimal = convertLand(plot.amount, plot.unitId, "decimal");
              return (
                <div key={plot.id} className="rounded-xl border bg-card p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className={`text-xs font-semibold uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                      {lang === "bn" ? `প্লট ${num(idx + 1)}` : `Plot ${idx + 1}`}
                    </span>
                    {plots.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removePlot(plot.id)} className="h-7 gap-1 text-destructive">
                        <Trash2 size={12} /> {t(STR.removePlot)}
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input placeholder={t(STR.plotName)} value={plot.name} onChange={(e) => updatePlot(plot.id, { name: e.target.value })} className="min-w-0" />
                    <Input placeholder={t(STR.plotLocation)} value={plot.location} onChange={(e) => updatePlot(plot.id, { location: e.target.value })} className="min-w-0" />
                    <select
                      value={plot.type}
                      onChange={(e) => updatePlot(plot.id, { type: e.target.value as LandType })}
                      className="h-9 w-full min-w-0 rounded-md border bg-background px-2 text-sm"
                    >
                      <option value="">{t(STR.plotType)}</option>
                      {(Object.keys(LAND_TYPE_LABELS) as Array<keyof typeof LAND_TYPE_LABELS>).map((k) => (
                        <option key={k} value={k}>{LAND_TYPE_LABELS[k][lang]}</option>
                      ))}
                    </select>
                    <select
                      value={plot.titleStatus}
                      onChange={(e) => updatePlot(plot.id, { titleStatus: e.target.value as LandTitleStatus })}
                      className="h-9 w-full min-w-0 rounded-md border bg-background px-2 text-sm"
                    >
                      <option value="">{t(STR.plotTitle)}</option>
                      {(Object.keys(TITLE_STATUS_LABELS) as Array<keyof typeof TITLE_STATUS_LABELS>).map((k) => (
                        <option key={k} value={k}>{TITLE_STATUS_LABELS[k][lang]}</option>
                      ))}
                    </select>
                    <div className="flex min-w-0 gap-2">
                      <Input
                        type="number"
                        inputMode="decimal"
                        placeholder={t(STR.plotArea)}
                        value={plot.amount || ""}
                        onChange={(e) => updatePlot(plot.id, { amount: Math.max(0, Number(e.target.value) || 0) })}
                        className="min-w-0 flex-1"
                      />
                      <select
                        value={plot.unitId}
                        onChange={(e) => updatePlot(plot.id, { unitId: e.target.value })}
                        className="h-9 min-w-0 max-w-[55%] rounded-md border bg-background px-2 text-sm"
                      >
                        {LAND_UNITS.map((u) => (
                          <option key={u.id} value={u.id}>{lang === "bn" ? u.bn : u.en}</option>
                        ))}
                      </select>
                    </div>
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder={t(STR.plotPrice)}
                      value={plot.pricePerUnit || ""}
                      onChange={(e) => updatePlot(plot.id, { pricePerUnit: Math.max(0, Number(e.target.value) || 0) })}
                    />
                  </div>
                  {plot.amount > 0 && unit && (
                    <p className={`mt-2 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                      = {num(formatLand(decimal, UNITS_BY_ID.decimal))} {lang === "bn" ? "ডেসিমেল" : "decimal"}
                      {" · "}
                      {num(formatLand(sqft, UNITS_BY_ID.sqft))} {lang === "bn" ? "বর্গফুট" : "sq ft"}
                      {plot.pricePerUnit > 0 && (
                        <> {" · "}{fmtBdt(plot.pricePerUnit * plot.amount)}</>
                      )}
                    </p>
                  )}
                  <label className={`mt-2 flex items-center gap-2 text-xs ${lang === "bn" ? "font-bn" : ""}`}>
                    <input
                      type="checkbox"
                      checked={plot.indivisible}
                      onChange={(e) => updatePlot(plot.id, { indivisible: e.target.checked })}
                    />
                    {t(STR.plotIndivisible)}
                  </label>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addPlot} className="gap-1">
              <Plus size={14} /> {t(STR.addPlot)}
            </Button>
          </div>
        </div>
      )}

      {/* Heirs */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Spouse */}
        <Section title={t(STR.spouse)} lang={lang}>
          <ToggleRow label={t(STR.husband)} checked={heirs.husband} onChange={(v) => setH("husband", v)} lang={lang} />
          <NumRow label={lang === "bn" ? "স্ত্রী (১–৪)" : "Wife/Wives (1–4)"} value={heirs.wives} onChange={(v) => setH("wives", Math.min(4, Math.max(0, v)))} max={4} lang={lang} disabled={heirs.husband} />
        </Section>

        {/* Children */}
        <Section title={t(STR.children)} lang={lang}>
          <NumRow label={HEIR_LABELS.sons[lang]} value={heirs.sons} onChange={(v) => setH("sons", v)} lang={lang} />
          <NumRow label={HEIR_LABELS.daughters[lang]} value={heirs.daughters} onChange={(v) => setH("daughters", v)} lang={lang} />
        </Section>

        {/* Parents */}
        <Section title={t(STR.parents)} lang={lang}>
          <ToggleRow label={t(STR.fatherAlive)} checked={heirs.father} onChange={(v) => setH("father", v)} lang={lang} />
          <ToggleRow label={t(STR.motherAlive)} checked={heirs.mother} onChange={(v) => setH("mother", v)} lang={lang} />
        </Section>

        {/* Grandparents */}
        <Section title={t(STR.grandparents)} lang={lang}>
          <ToggleRow label={t(STR.pgf)} checked={heirs.paternalGrandfather} onChange={(v) => setH("paternalGrandfather", v)} lang={lang} disabled={heirs.father} />
          <ToggleRow label={t(STR.pgm)} checked={heirs.paternalGrandmother} onChange={(v) => setH("paternalGrandmother", v)} lang={lang} disabled={heirs.mother || heirs.father} />
          <ToggleRow label={t(STR.mgm)} checked={heirs.maternalGrandmother} onChange={(v) => setH("maternalGrandmother", v)} lang={lang} disabled={heirs.mother} />
        </Section>

        {/* Grandchildren */}
        <Section title={t(STR.grandchildren)} lang={lang}>
          <NumRow label={HEIR_LABELS.sonsSons[lang]} value={heirs.sonsSons} onChange={(v) => setH("sonsSons", v)} lang={lang} disabled={heirs.sons > 0} />
          <NumRow label={HEIR_LABELS.sonsDaughters[lang]} value={heirs.sonsDaughters} onChange={(v) => setH("sonsDaughters", v)} lang={lang} disabled={heirs.sons > 0} />
        </Section>

        {/* Siblings */}
        <Section title={t(STR.siblings)} lang={lang}>
          <NumRow label={HEIR_LABELS.fullBrothers[lang]} value={heirs.fullBrothers} onChange={(v) => setH("fullBrothers", v)} lang={lang} />
          <NumRow label={HEIR_LABELS.fullSisters[lang]} value={heirs.fullSisters} onChange={(v) => setH("fullSisters", v)} lang={lang} />
          <NumRow label={HEIR_LABELS.paternalHalfBrothers[lang]} value={heirs.paternalHalfBrothers} onChange={(v) => setH("paternalHalfBrothers", v)} lang={lang} />
          <NumRow label={HEIR_LABELS.paternalHalfSisters[lang]} value={heirs.paternalHalfSisters} onChange={(v) => setH("paternalHalfSisters", v)} lang={lang} />
          <NumRow label={HEIR_LABELS.maternalHalfSiblings[lang]} value={heirs.maternalHalfSiblings} onChange={(v) => setH("maternalHalfSiblings", v)} lang={lang} />
        </Section>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={reset}>{t(STR.reset)}</Button>
        <Button variant="outline" size="sm" onClick={() => typeof window !== "undefined" && window.print()}>{t(STR.print)}</Button>
      </div>

      {/* Results */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-2xl border bg-card shadow-sm">
        <div className="border-b p-5">
          <h2 className={`text-xl font-semibold ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.results)}</h2>
          <div className={`mt-1 text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
            {lang === "bn" ? "মোট সম্পত্তি: " : "Total estate: "}<strong>{fmtBdt(estate)}</strong>
            {" · "}
            {lang === "bn" ? "উত্তরাধিকারী: " : "Heirs: "}<strong>{num(result.shares.reduce((a, l) => a + l.count, 0))}</strong>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {result.awlApplied && <Tag tone="warning" lang={lang}>{t(STR.awlTag)}</Tag>}
            {result.raddApplied && <Tag tone="info" lang={lang}>{t(STR.raddTag)}</Tag>}
            {result.asabahApplied && <Tag tone="success" lang={lang}>{t(STR.asabahTag)}</Tag>}
          </div>
        </div>

        {result.shares.length === 0 ? (
          <p className={`p-5 text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
            {lang === "bn" ? "কোনো উত্তরাধিকারী যোগ করুন।" : "Add at least one heir to see the distribution."}
          </p>
        ) : (
          <>
            <div className="grid gap-4 p-5 md:grid-cols-[1fr_280px]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className={`pb-2 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.table.heir)}</th>
                      <th className={`pb-2 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.table.share)}</th>
                      <th className={`pb-2 text-right ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.table.amount)}</th>
                      <th className={`pb-2 text-right ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.table.perPerson)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.shares.map((l, i) => (
                      <tr key={`${l.group}-${l.type}-${i}`} className="border-t">
                        <td className="py-2">
                          <span className="inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className={`ml-2 ${lang === "bn" ? "font-bn" : ""}`}>
                            {lang === "bn" ? HEIR_LABELS[l.group].bn : HEIR_LABELS[l.group].en}
                            {l.count > 1 && <span className="text-muted-foreground"> ×{num(l.count)}</span>}
                          </span>
                          <div className={`pl-4 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{lang === "bn" ? l.rule.bn : l.rule.en}</div>
                        </td>
                        <td className={`py-2 ${lang === "bn" ? "font-bn" : ""}`}>{l.fraction.toString()}</td>
                        <td className="py-2 text-right tabular-nums">{fmtBdt(l.amount)}</td>
                        <td className="py-2 text-right tabular-nums">{l.count > 1 ? fmtBdt(l.amountPer) : "—"}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-muted/30">
                      <td className={`py-2 font-medium ${lang === "bn" ? "font-bn" : ""}`} colSpan={2}>{t(STR.totalCheck)}</td>
                      <td className="py-2 text-right font-semibold tabular-nums">{fmtBdt(total)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="h-64 md:h-auto">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={90} paddingAngle={2}>
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <RTooltip formatter={(v: number) => fmtBdt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {result.blocked.length > 0 && (
              <div className={`border-t p-5 ${lang === "bn" ? "font-bn" : ""}`}>
                <h3 className="mb-2 text-sm font-semibold">{t(STR.blocked)}</h3>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {result.blocked.map((b, i) => (
                    <li key={i}>
                      <strong className="text-foreground">{lang === "bn" ? HEIR_LABELS[b.group].bn : HEIR_LABELS[b.group].en}{b.count > 1 ? ` ×${num(b.count)}` : ""}</strong> — {lang === "bn" ? b.reason.bn : b.reason.en}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Step-by-step */}
            <div className={`border-t p-5 ${lang === "bn" ? "font-bn" : ""}`}>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <ScrollText size={16} className="text-accent" /> {t(STR.why)}
              </h3>
              <ol className="space-y-1 text-sm text-muted-foreground">
                {result.steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-accent">{num(i + 1)}.</span>
                    <span>{lang === "bn" ? s.bn : s.en}</span>
                  </li>
                ))}
              </ol>
            </div>

            {result.notes.length > 0 && (
              <div className={`border-t bg-accent/5 p-5 text-sm ${lang === "bn" ? "font-bn" : ""}`}>
                {result.notes.map((n, i) => (
                  <p key={i} className="text-muted-foreground"><Info size={14} className="mr-1 inline text-accent" />{lang === "bn" ? n.bn : n.en}</p>
                ))}
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Educational content */}
      <section className={`mt-12 space-y-8 ${lang === "bn" ? "font-bn" : ""}`}>
        <Article lang={lang}
          title={{ en: "What is Faraid?", bn: "ফরায়েজ কী?" }}
          body={{
            en: "Faraid (al-fara'id) is the Islamic science of obligatory inheritance. The shares are fixed by the Qur'an and Sunnah, not by the deceased's will. After funeral expenses, debts, and bequests (max 1/3) are settled, the remaining estate is distributed strictly according to these rules.",
            bn: "ফরায়েজ (আল-ফরাইজ) হলো ইসলামের বাধ্যতামূলক উত্তরাধিকার বিজ্ঞান। অংশগুলো কুরআন ও সুন্নাহ দ্বারা নির্ধারিত — মৃতের ইচ্ছা নয়। দাফন খরচ, ঋণ ও ওয়াসিয়াত (সর্বোচ্চ ১/৩) মিটানোর পর অবশিষ্ট সম্পত্তি কঠোরভাবে এই বিধান অনুযায়ী বণ্টিত হয়।",
          }} />
        <Article lang={lang}
          title={{ en: "Furud — fixed Quranic shares", bn: "ফরাইজ — কুরআনে নির্ধারিত অংশ" }}
          body={{
            en: "Six fixed fractions are mentioned in the Qur'an: 1/2, 1/4, 1/8, 2/3, 1/3, 1/6. Heirs entitled to a Furud share include the spouse, parents, daughters, son's daughters, sisters, and grandmothers. Each share depends on which other heirs are also present.",
            bn: "কুরআনে ছয়টি নির্দিষ্ট ভগ্নাংশ উল্লেখ আছে: ১/২, ১/৪, ১/৮, ২/৩, ১/৩, ১/৬। ফরাইজ-অংশীদারদের মধ্যে আছেন স্বামী/স্ত্রী, পিতা-মাতা, কন্যা, পৌত্রী, বোন ও দাদি/নানি। প্রতিটি অংশ অন্য কোন উত্তরাধিকারী আছেন তার উপর নির্ভর করে।",
          }} />
        <Article lang={lang}
          title={{ en: "Asabah — residuary heirs", bn: "আসাবা — অবশিষ্ট-গ্রহীতা" }}
          body={{
            en: "After Furud shares are paid, what remains goes to the Asabah — the male agnatic line: sons, son's sons, father, grandfather, full brothers, half-brothers (paternal), uncles. Females become Asabah alongside their brothers in 2:1 ratio (Asabah-bil-ghayr).",
            bn: "ফরাইজ পরিশোধের পর অবশিষ্ট অংশ পান আসাবা — পুরুষ বংশধারা: পুত্র, পৌত্র, পিতা, দাদা, সহোদর ভাই, বৈমাত্রেয় ভাই, চাচা প্রভৃতি। নারীরা ভাইদের সাথে ২ঃ১ অনুপাতে আসাবা হন (আসাবা-বিল-গায়র)।",
          }} />
        <Article lang={lang}
          title={{ en: "Hajb — blocking", bn: "হাজব — অংশ-নিষেধ" }}
          body={{
            en: "An heir may be partially or completely excluded by another, closer heir. Examples: father blocks paternal grandfather; mother blocks both grandmothers; son blocks his own son and daughter; full brother blocks paternal half-brothers; descendants and the father block all siblings.",
            bn: "একজন উত্তরাধিকারী আরেকজন (আরো নিকটতর) উত্তরাধিকারী দ্বারা সম্পূর্ণ বা আংশিক বাদ পড়তে পারেন। উদাহরণ: পিতা দাদাকে বাদ দেন; মাতা দু'জন গ্র্যান্ডমাকে বাদ দেন; পুত্র তার নিজের সন্তানদের বাদ দেন; সহোদর ভাই বৈমাত্রেয় ভাইদের বাদ দেন; সন্তান ও পিতা সব ভাই-বোনকে বাদ দেন।",
          }} />
        <Article lang={lang}
          title={{ en: "Awl & Radd", bn: "আউল ও রাদ্দ" }}
          body={{
            en: "When the Furud shares add up to more than 1, all are reduced proportionally — this is Awl. When they add to less than 1 with no Asabah, the surplus returns proportionally to the Furud heirs (excluding the spouse) — this is Radd.",
            bn: "ফরাইজের যোগফল ১ এর বেশি হলে সবগুলো আনুপাতিকভাবে কমে — একে আউল বলে। যোগফল ১ এর কম এবং কোনো আসাবা না থাকলে অবশিষ্ট অংশ স্বামী/স্ত্রী বাদে অন্য ফরাইজ-অংশীদারদের ফিরে যায় — একে রাদ্দ বলে।",
          }} />
        <Article lang={lang}
          title={{ en: "Wasiyyah (Islamic will) up to 1/3", bn: "ওয়াসিয়াত — সর্বোচ্চ ১/৩" }}
          body={{
            en: "A Muslim may will up to 1/3 of their net estate to anyone who is NOT a Quranic heir (e.g., a charity, a non-Muslim relative, or an adopted child). A bequest beyond 1/3 — or to an existing heir — is invalid unless all other heirs unanimously agree after death.",
            bn: "একজন মুসলিম তার নীট সম্পত্তির সর্বোচ্চ ১/৩ পর্যন্ত এমন কাউকে ওয়াসিয়াত করতে পারেন যিনি ফরাইজ উত্তরাধিকারী নন (যেমন দাতব্য, অমুসলিম আত্মীয়, দত্তক সন্তান)। ১/৩ এর বেশি — বা ফরাইজ উত্তরাধিকারীর জন্য — ওয়াসিয়াত অবৈধ, যদি না অন্য সব উত্তরাধিকারী মৃত্যুর পর ঐক্যমতে রাজি হন।",
          }} />
        <Article lang={lang}
          title={{ en: "Bangladesh Muslim Family Laws Ordinance 1961", bn: "বাংলাদেশের মুসলিম পারিবারিক আইন অধ্যাদেশ ১৯৬১" }}
          body={{
            en: "The Ordinance recognises Sharia Faraid for Muslims in Bangladesh, with one notable modification: orphaned grandchildren (whose parent — the deceased's son or daughter — predeceased them) inherit per stirpes the share their parent would have received. This calculator does NOT yet model that statutory exception.",
            bn: "এই অধ্যাদেশ বাংলাদেশের মুসলিমদের জন্য শরীয়াহ ফরাইজ স্বীকৃতি দেয়, একটি উল্লেখযোগ্য পরিবর্তনসহ: এতিম পৌত্র-পৌত্রী (যাদের পিতা-মাতা মৃতের আগেই মারা গেছেন) তাদের পিতা-মাতা যা পেতেন সেই অংশ পায়। এই ক্যালকুলেটর সেই আইনি ব্যতিক্রম এখনো অন্তর্ভুক্ত করেনি।",
          }} />
        <Article lang={lang}
          title={{ en: "Limitations of this calculator", bn: "এই ক্যালকুলেটরের সীমাবদ্ধতা" }}
          body={{
            en: "v1 implements Hanafi rules for the standard cases (Furud, Asabah, Hajb, Awl, Radd, Umariyatayn). NOT yet modelled: grandfather-vs-brothers competition (Maliki/Shafi'i/Hanbali), Mushtarakah, Akdariyya, Dhul-Arham (distant kin), the BD Ordinance 1961 grandchild exception, and missing/disqualified heirs (apostate, slayer of deceased).",
            bn: "v1-এ স্ট্যান্ডার্ড কেসের জন্য হানাফী বিধান (ফরাইজ, আসাবা, হাজব, আউল, রাদ্দ, উমারিয়াতাইন) অন্তর্ভুক্ত। এখনো অন্তর্ভুক্ত নয়: দাদা-ভাইয়ের প্রতিযোগিতা (মালিকী/শাফি'ঈ/হাম্বলী), মুশতারাকা, আকদারিয়া, যবিল আরহাম, BD অধ্যাদেশ ১৯৬১-এর পৌত্র-পৌত্রী ব্যতিক্রম, এবং অযোগ্য উত্তরাধিকারী (ধর্মত্যাগী, হত্যাকারী)।",
          }} />

        <div className={`rounded-xl border-2 border-destructive/30 bg-destructive/5 p-4 text-sm`}>
          <p className="flex items-start gap-2 font-medium text-destructive">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {lang === "bn"
              ? "এই ক্যালকুলেটর শিক্ষামূলক। প্রকৃত উত্তরাধিকার বণ্টনের আগে অভিজ্ঞ মুফতি ও আইনজীবীর সাথে পরামর্শ করুন এবং উপযুক্ত আদালত থেকে succession certificate নিন।"
              : "This calculator is for educational purposes only. Before any actual distribution, consult a qualified Mufti and lawyer, and obtain a succession certificate from the appropriate court."}
          </p>
        </div>

        <div className="rounded-xl border bg-accent/5 p-4 text-sm">
          <p className={`flex items-center gap-2 ${lang === "bn" ? "font-bn" : ""}`}>
            <BookOpen size={16} className="text-accent" />
            {lang === "bn" ? "সম্পর্কিত: " : "Related: "}
            <Link to="/zakat-calculator" className="underline">{lang === "bn" ? "যাকাত" : "Zakat"}</Link>
            {" · "}
            <Link to="/hijri-date-converter" className="underline">{lang === "bn" ? "হিজরি তারিখ" : "Hijri Date"}</Link>
            {" · "}
            <Link to="/tax-calculator" className="underline">{lang === "bn" ? "আয়কর" : "Income Tax"}</Link>
            {" · "}
            <Link to="/land-calculator" className="underline">{lang === "bn" ? "জমি" : "Land"}</Link>
          </p>
        </div>
      </section>
    </CalculatorLayout>
  );
}

function Section({ title, lang, children }: { title: string; lang: "en" | "bn"; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className={`mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, lang, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; lang: "en" | "bn"; disabled?: boolean }) {
  return (
    <label className={`flex items-center justify-between gap-3 text-sm ${disabled ? "opacity-40" : ""}`}>
      <span className={lang === "bn" ? "font-bn" : ""}>{label}</span>
      <Switch checked={checked && !disabled} onCheckedChange={onChange} disabled={disabled} />
    </label>
  );
}

function NumRow({ label, value, onChange, lang, max, disabled }: { label: string; value: number; onChange: (v: number) => void; lang: "en" | "bn"; max?: number; disabled?: boolean }) {
  return (
    <label className={`flex items-center justify-between gap-3 text-sm ${disabled ? "opacity-40" : ""}`}>
      <span className={lang === "bn" ? "font-bn" : ""}>{label}</span>
      <Input type="number" min={0} max={max} value={value || 0} onChange={(e) => onChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))} className="h-8 w-20 text-right" disabled={disabled} />
    </label>
  );
}

function Tag({ children, tone, lang }: { children: React.ReactNode; tone: "warning" | "info" | "success"; lang: "en" | "bn" }) {
  const tones = {
    warning: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    info: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
    success: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  };
  return <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${tones[tone]} ${lang === "bn" ? "font-bn" : ""}`}>{children}</span>;
}

function Article({ title, body, lang }: { title: Bilingual; body: Bilingual; lang: "en" | "bn" }) {
  return (
    <div>
      <h2 className="text-xl font-semibold">{lang === "bn" ? title.bn : title.en}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {lang === "bn" ? body.bn : body.en}
      </p>
    </div>
  );
}
